/**
 * Thoughts Context
 *
 * Bridges chain-of-thought data from the message thread to the side panel.
 * Supports multiple concurrent thought instances (one per AgentChainOfThought)
 * with click-to-select toggling.
 *
 * Uses a ref-backed map to avoid re-renders on every streaming delta.
 * State updates only fire when meaningful changes occur (step count changes,
 * isRunning transitions).
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ThinkingStep } from '../components/assistant-ui/agent-chain-of-thought';

export interface ThoughtsData {
  steps: ThinkingStep[];
  elapsed: number;
  isRunning: boolean;
  runId?: string;
  error?: string;
}

interface ThoughtsContextValue {
  /** The currently selected thought's data (derived from map + activeThoughtId). */
  thoughts: ThoughtsData | null;
  /** Which thought instance is selected, or null if none. */
  activeThoughtId: string | null;
  /** Register/update a thought instance's data (called from AgentChainOfThought). */
  registerThoughts: (id: string, data: ThoughtsData) => void;
  /** Remove a thought instance on unmount. */
  unregisterThoughts: (id: string) => void;
  /** Update just the steps for a thought instance (used for lazy-loaded run details). */
  updateThoughtsSteps: (id: string, steps: ThinkingStep[]) => void;
  /** Toggle selection: same ID → deselect & close panel; different ID → select & open. */
  selectThought: (id: string) => void;
  /** Whether the thoughts panel is visible. */
  thoughtsActive: boolean;
  setThoughtsActive: (v: boolean) => void;
  /**
   * True when a surface that can display a selected thought (e.g. the Sandbox
   * panel) is mounted. The collapsed "Thought for Xs" header only renders as a
   * clickable control when a panel exists to receive the selection — elsewhere
   * the click would be a no-op, so it renders as plain text instead.
   */
  panelMounted: boolean;
  /** Called by a thoughts panel on mount; returns an unregister cleanup. */
  registerPanel: () => () => void;
}

const ThoughtsContext = createContext<ThoughtsContextValue | null>(null);

export function ThoughtsProvider({ children }: { children: ReactNode }) {
  // Ref-backed map: avoids creating a new Map on every streaming event.
  // We only trigger a state version bump when meaningful changes occur.
  const mapRef = useRef(new Map<string, ThoughtsData>());
  const [mapVersion, setMapVersion] = useState(0);
  const [activeThoughtId, setActiveThoughtId] = useState<string | null>(null);
  const [thoughtsActive, setThoughtsActive] = useState(false);

  // Ref-counted so multiple/remounting panels don't flip the flag off early.
  const panelCountRef = useRef(0);
  const [panelMounted, setPanelMounted] = useState(false);
  const registerPanel = useCallback(() => {
    panelCountRef.current += 1;
    setPanelMounted(true);
    return () => {
      panelCountRef.current -= 1;
      if (panelCountRef.current <= 0) setPanelMounted(false);
    };
  }, []);

  const registerThoughts = useCallback((id: string, data: ThoughtsData) => {
    const prev = mapRef.current.get(id);
    mapRef.current.set(id, data);

    // Only bump version on meaningful changes: step count, isRunning, or elapsed (integer seconds)
    const changed =
      !prev ||
      (prev.steps?.length ?? 0) !== (data.steps?.length ?? 0) ||
      prev.isRunning !== data.isRunning ||
      prev.elapsed !== data.elapsed ||
      prev.error !== data.error;

    if (changed) {
      setMapVersion((v) => v + 1);
    }
  }, []);

  const unregisterThoughts = useCallback((id: string) => {
    mapRef.current.delete(id);
    setMapVersion((v) => v + 1);
    setActiveThoughtId((prev) => (prev === id ? null : prev));
  }, []);

  const updateThoughtsSteps = useCallback((id: string, steps: ThinkingStep[]) => {
    const entry = mapRef.current.get(id);
    if (entry) {
      mapRef.current.set(id, { ...entry, steps });
      setMapVersion((v) => v + 1);
    }
  }, []);

  const selectThought = useCallback((id: string) => {
    setActiveThoughtId((prev) => {
      if (prev === id) {
        // Deselect → close panel
        setThoughtsActive(false);
        return null;
      }
      // Select new → open panel
      setThoughtsActive(true);
      return id;
    });
  }, []);

  const thoughts = useMemo(
    () => (activeThoughtId ? mapRef.current.get(activeThoughtId) ?? null : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeThoughtId, mapVersion],
  );

  const value = useMemo<ThoughtsContextValue>(
    () => ({
      thoughts,
      activeThoughtId,
      registerThoughts,
      unregisterThoughts,
      updateThoughtsSteps,
      selectThought,
      thoughtsActive,
      setThoughtsActive,
      panelMounted,
      registerPanel,
    }),
    [thoughts, activeThoughtId, registerThoughts, unregisterThoughts, updateThoughtsSteps, selectThought, thoughtsActive, panelMounted, registerPanel],
  );

  return (
    <ThoughtsContext.Provider value={value}>
      {children}
    </ThoughtsContext.Provider>
  );
}

export function useThoughts(): ThoughtsContextValue {
  const context = useContext(ThoughtsContext);
  if (!context) {
    throw new Error('useThoughts must be used within a ThoughtsProvider');
  }
  return context;
}
